import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Icon } from '@uplink/ui';
import { useEffect, useRef, useState } from 'react';
import { API_URL, useTRPC } from '../api.ts';
import logo from '../assets/uplink-logo.svg';
import { usePrefs } from '../prefs.tsx';

const ageHours = (date: string) => (Date.now() - Date.parse(date)) / 3_600_000;
const MIN_WORKING_SPEED_MBPS = 1;
type CheckState = 'idle' | 'checking' | 'ok' | 'fail';
type ConnectionNotice = 'checking' | 'connected' | 'missing' | null;
type ServiceKey = 'telegram' | 'youtube' | 'instagram' | 'tiktok' | 'discord';
type ServiceStates = Record<ServiceKey, CheckState>;
const SERVICES: { key: ServiceKey; name: string; url: string }[] = [
  { key: 'telegram', name: 'Telegram', url: 'https://telegram.org/' },
  { key: 'youtube', name: 'YouTube', url: 'https://www.youtube.com/generate_204' },
  { key: 'instagram', name: 'Instagram', url: 'https://www.instagram.com/favicon.ico' },
  { key: 'tiktok', name: 'TikTok', url: 'https://www.tiktok.com/favicon.ico' },
  { key: 'discord', name: 'Discord', url: 'https://discord.com/favicon.ico' },
];

const emptyServices = (): ServiceStates => ({
  telegram: 'idle',
  youtube: 'idle',
  instagram: 'idle',
  tiktok: 'idle',
  discord: 'idle',
});

function deviceCoordinates(): Promise<{ latitude: number; longitude: number } | null> {
  if (!navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: false, maximumAge: 0, timeout: 10_000 },
    );
  });
}

async function serviceAvailable(url: string): Promise<boolean> {
  try {
    await fetch(`${url}${url.includes('?') ? '&' : '?'}uplink-check=${Date.now()}`, {
      cache: 'no-store',
      mode: 'no-cors',
      signal: AbortSignal.timeout(10_000),
    });
    return true;
  } catch {
    return false;
  }
}

async function measureDownload(token: string | null, onProgress: (mbps: number) => void) {
  const baseUrl = API_URL.replace(/\/$/, '');
  const startedAt = performance.now();
  const response = await fetch(`${baseUrl}/vpn-check/download?t=${Date.now()}`, {
    cache: 'no-store',
    headers: token ? { 'x-vpn-check-token': token } : undefined,
    signal: AbortSignal.timeout(30_000),
  });
  if (!response.ok) throw new Error('Тестовый файл не загрузился');
  let bytes = 0;
  if (response.body) {
    const reader = response.body.getReader();
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      bytes += chunk.value.byteLength;
      const seconds = (performance.now() - startedAt) / 1000;
      if (seconds > 0) onProgress(Math.round(((bytes * 8) / seconds / 1_000_000) * 10) / 10);
    }
  } else {
    bytes = (await response.arrayBuffer()).byteLength;
  }
  const seconds = (performance.now() - startedAt) / 1000;
  if (!bytes || seconds <= 0) throw new Error('Не удалось измерить скорость');
  return Math.round(((bytes * 8) / seconds / 1_000_000) * 10) / 10;
}

/** Публичная диагностика не раскрывает IP, облачного провайдера и приватную ссылку Speedtest. */
export function VpnStatusPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { lang } = usePrefs();
  const status = useQuery(trpc.vpnRussiaStatus.queryOptions());
  const me = useQuery({ ...trpc.me.queryOptions(), retry: false });
  const connection = useQuery({ ...trpc.vpnConnection.queryOptions(), refetchInterval: 10_000 });
  const trial = useQuery({ ...trpc.vpnTrial.get.queryOptions(), enabled: !!me.data, retry: false });
  const checkToken = new URLSearchParams(window.location.hash.slice(1)).get('check');
  const [phase, setPhase] = useState<string | null>(null);
  const [speed, setSpeed] = useState<number | null>(null);
  const [services, setServices] = useState<ServiceStates>(emptyServices);
  const [personalWorks, setPersonalWorks] = useState<boolean | null>(null);
  const [location, setLocation] = useState<string | null>(null);
  const [locationUnavailable, setLocationUnavailable] = useState(false);
  const [locationOutsideRussia, setLocationOutsideRussia] = useState(false);
  const [locationVerifiedInRussia, setLocationVerifiedInRussia] = useState(false);
  const [checkFeedback, setCheckFeedback] = useState<string | null>(null);
  const [testError, setTestError] = useState(false);
  const [connectionNotice, setConnectionNotice] = useState<ConnectionNotice>(null);
  const restoredCheckAt = useRef<string | null>(null);
  const submit = useMutation(
    trpc.submitVpnRussiaCheck.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries(trpc.vpnRussiaStatus.pathFilter()),
    }),
  );
  const issueTrial = useMutation(
    trpc.vpnTrial.issue.mutationOptions({
      onSuccess: () => trial.refetch(),
    }),
  );
  const value = status.data;
  const fresh = value ? ageHours(value.checkedAt) <= 48 : false;
  const working = !!value?.works && fresh;
  const failed = value?.works === false && fresh;
  const ru = lang === 'ru';
  const checkedInRussia = value?.region?.toLocaleLowerCase('ru').includes('россия') ?? false;
  const locationParts = value?.region
    ?.split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const checkedCity = locationParts?.[0] ?? (ru ? 'Россия' : 'Russia');
  const checkedCountry = locationParts?.at(-1) ?? (ru ? 'Россия' : 'Russia');
  const checkedRegion =
    locationParts && locationParts.length > 2
      ? locationParts.slice(1, -1).join(', ')
      : (locationParts?.[1] ?? (ru ? 'Россия' : 'Russia'));
  const successTone =
    value?.successPercent === undefined
      ? ''
      : value.successPercent > 50
        ? ' is-success'
        : value.successPercent < 25
          ? ' is-danger'
          : ' is-warning';

  useEffect(() => {
    if (!value || restoredCheckAt.current === value.checkedAt) return;
    restoredCheckAt.current = value.checkedAt;
    setSpeed(value.downloadMbps);
    setPersonalWorks(value.works);
    setServices({
      telegram: value.telegramWorks ? 'ok' : 'fail',
      youtube: value.youtubeWorks ? 'ok' : 'fail',
      instagram: value.instagramWorks === undefined ? 'idle' : value.instagramWorks ? 'ok' : 'fail',
      tiktok: value.tiktokWorks === undefined ? 'idle' : value.tiktokWorks ? 'ok' : 'fail',
      discord: value.discordWorks === undefined ? 'idle' : value.discordWorks ? 'ok' : 'fail',
    });
  }, [value]);

  const runCheck = async () => {
    submit.reset();
    setCheckFeedback(null);
    setTestError(false);
    setPhase(ru ? 'Проверяем подключение и геолокацию…' : 'Checking connection and location…');
    setConnectionNotice('checking');
    const currentConnection = await connection.refetch();
    if (!currentConnection.data?.connected) {
      setConnectionNotice('missing');
      setCheckFeedback(
        ru
          ? 'Проверка не запущена: сначала подключитесь к Uplink VPN.'
          : 'The check did not start: connect to Uplink VPN first.',
      );
      setPhase(null);
      return;
    }
    setConnectionNotice('connected');
    setLocationUnavailable(false);
    setLocationOutsideRussia(false);
    setLocationVerifiedInRussia(false);
    const coordinates = await deviceCoordinates();
    if (!coordinates) {
      setLocationUnavailable(true);
      setLocation(null);
    }
    let detectedLocation: {
      region: string;
      countryCode: string;
      locationToken: string | null;
    } | null = null;
    if (coordinates) {
      try {
        detectedLocation = await queryClient.fetchQuery(trpc.vpnLocation.queryOptions(coordinates));
      } catch {
        detectedLocation = null;
      }
    }
    setLocationUnavailable(!detectedLocation);
    setLocationOutsideRussia(!!detectedLocation && detectedLocation.countryCode !== 'ru');
    setLocationVerifiedInRussia(!!detectedLocation?.locationToken);
    setLocation(detectedLocation?.region ?? null);
    setPersonalWorks(null);
    setSpeed(null);
    setServices(emptyServices());
    setPhase(ru ? 'Измеряем скорость…' : 'Measuring speed…');
    let downloadMbps: number | null = null;
    try {
      downloadMbps = await measureDownload(checkToken, setSpeed);
      setSpeed(downloadMbps);
    } catch {
      setTestError(true);
    }

    setPhase(ru ? 'Проверяем доступность сервисов…' : 'Checking service availability…');
    setServices({
      telegram: 'checking',
      youtube: 'checking',
      instagram: 'checking',
      tiktok: 'checking',
      discord: 'checking',
    });
    const results = await Promise.all(
      SERVICES.map(async ({ key, url }) => {
        const available = await serviceAvailable(url);
        setServices((current) => ({ ...current, [key]: available ? 'ok' : 'fail' }));
        return [key, available] as const;
      }),
    );
    const availability = Object.fromEntries(results) as Record<ServiceKey, boolean>;
    const works =
      downloadMbps !== null && downloadMbps >= MIN_WORKING_SPEED_MBPS && results.some(([, ok]) => ok);
    setPersonalWorks(works);
    if (detectedLocation?.locationToken) {
      setPhase(ru ? 'Сохраняем результат проверки…' : 'Saving the check result…');
      try {
        await submit.mutateAsync({
          token: checkToken ?? undefined,
          locationToken: detectedLocation.locationToken,
          works: works && availability.telegram && availability.youtube,
          downloadMbps,
          telegramWorks: availability.telegram,
          youtubeWorks: availability.youtube,
          instagramWorks: availability.instagram,
          tiktokWorks: availability.tiktok,
          discordWorks: availability.discord,
          device: navigator.userAgent.slice(0, 100),
        });
      } catch {
        setTestError(true);
      }
    }
    setPhase(null);
  };

  return (
    <main className="lp lp-status-page">
      <section className="lp-status-card">
        <a className="lp-brand" href="/">
          <img src={logo} alt="" width={34} height={34} /> Uplink
        </a>
        <span className={`lp-status-mark${working ? ' is-ok' : ''}`}>
          <Icon name={working ? 'check' : 'alert'} size={28} />
        </span>
        <h1 className="lp-grad">
          {working
            ? ru
              ? checkedInRussia
                ? 'VPN работает в России'
                : 'Uplink VPN работает'
              : checkedInRussia
                ? 'VPN works in Russia'
                : 'Uplink VPN works'
            : failed
              ? ru
                ? 'Зафиксирована проблема с VPN'
                : 'A VPN issue has been reported'
              : ru
                ? 'Нужна новая проверка'
                : 'A new check is needed'}
        </h1>
        <p>
          {working
            ? ru
              ? checkedInRussia
                ? 'Подключение проверено реальным человеком из России.'
                : 'Последняя проверка выполнена через Uplink VPN.'
              : checkedInRussia
                ? 'The connection was checked by a real person in Russia.'
                : 'The latest check was performed through Uplink VPN.'
            : failed
              ? ru
                ? checkedInRussia
                  ? 'Последняя проверка из России завершилась неудачно.'
                  : 'Последняя проверка Uplink VPN завершилась неудачно.'
                : checkedInRussia
                  ? 'The latest check from Russia failed.'
                  : 'The latest Uplink VPN check failed.'
              : ru
                ? 'Мы не показываем устаревший результат как актуальный.'
                : 'We do not present an outdated result as current.'}
        </p>
        {value ? (
          <dl className="lp-status-facts">
            <div>
              <dt>{ru ? 'Проверено' : 'Checked'}</dt>
              <dd>{new Date(value.checkedAt).toLocaleString(ru ? 'ru-RU' : 'en-US')}</dd>
            </div>
            <div>
              <dt>{ru ? 'Местоположение' : 'Location'}</dt>
              <dd className="lp-status-location">
                <span>{checkedCountry}</span>
                {checkedRegion !== checkedCountry ? <span>{checkedRegion}</span> : null}
                {checkedCity !== checkedRegion && checkedCity !== checkedCountry ? (
                  <span>{checkedCity}</span>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>{ru ? 'Загрузка' : 'Download'}</dt>
              <dd>
                {value.downloadMbps !== null
                  ? `${value.downloadMbps} Mbps`
                  : ru
                    ? 'Не измерена'
                    : 'Not measured'}
              </dd>
            </div>
            <div>
              <dt>{ru ? 'Успешность проверок' : 'Check success rate'}</dt>
              <dd className={`lp-success-rate${successTone}`}>
                <strong>{value.successPercent ?? 0}%</strong>
                <span>
                  {ru
                    ? `по последним ${value.checksConsidered ?? 0} из 10`
                    : `across the latest ${value.checksConsidered ?? 0} of 10`}
                </span>
              </dd>
            </div>
          </dl>
        ) : null}

        <section className="lp-check-panel">
          <h2>{ru ? 'Проверьте VPN сами' : 'Check the VPN yourself'}</h2>
          <p>
            {ru
              ? 'Подключитесь к VPN и нажмите кнопку. Страница в реальном времени измерит скорость и проверит доступность популярных сервисов.'
              : 'Connect to the VPN and press the button. The page will measure speed and service availability in real time.'}
          </p>
          {locationOutsideRussia ? (
            <p className="lp-check-location">
              <Icon name="globe" size={18} /> {ru ? 'Местоположение устройства:' : 'Device location:'}{' '}
              {location}
            </p>
          ) : location ? (
            <p className="lp-check-location">
              <Icon name="globe" size={18} /> {ru ? 'Местоположение устройства:' : 'Device location:'}{' '}
              {location}
              {' · '}
              <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
                © OpenStreetMap
              </a>
            </p>
          ) : locationUnavailable ? (
            <p className="lp-check-location">
              <Icon name="alert" size={18} />{' '}
              {ru
                ? 'Местоположение устройства определить не удалось.'
                : 'The device location could not be determined.'}
            </p>
          ) : (
            <p className="lp-check-location">
              <Icon name="globe" size={18} />{' '}
              {ru
                ? 'При запуске браузер попросит разрешить геолокацию устройства.'
                : 'When the check starts, the browser will request device location permission.'}
            </p>
          )}
          {!connection.isPending && !connection.data?.connected ? (
            <div className="lp-vpn-gate">
              <h3>{ru ? 'Сначала подключитесь к Uplink VPN' : 'Connect to Uplink VPN first'}</h3>
              <p>
                {ru
                  ? 'Проверка измеряет именно соединение Uplink. Без него кнопка теста недоступна.'
                  : 'The check measures an Uplink connection, so it is unavailable without the VPN.'}
              </p>
              {!me.data ? (
                <a className="lp-btn lp-btn--primary" href="/?trial=1">
                  {ru
                    ? 'Зарегистрироваться и получить 24 часа бесплатно'
                    : 'Sign up for a free 24-hour trial'}
                </a>
              ) : trial.data?.status === 'active' && trial.data.key ? (
                <div className="lp-trial-key">
                  <strong>{ru ? 'Ваш пробный ключ' : 'Your trial key'}</strong>
                  <code>{trial.data.key}</code>
                  <Button
                    onClick={() => {
                      void navigator.clipboard.writeText(trial.data?.key ?? '');
                    }}
                  >
                    {ru ? 'Скопировать ключ' : 'Copy key'}
                  </Button>
                  <small>
                    {ru ? 'Работает до ' : 'Valid until '}
                    {new Date(trial.data.expiresAt).toLocaleString(ru ? 'ru-RU' : 'en-US')}
                  </small>
                </div>
              ) : trial.data?.status === 'expired' ? (
                <p>{ru ? 'Пробный период уже использован.' : 'The free trial has already been used.'}</p>
              ) : (
                <>
                  <Button
                    loading={issueTrial.isPending}
                    disabled={issueTrial.isPending}
                    onClick={() => issueTrial.mutate()}
                  >
                    {ru ? 'Получить ключ на 24 часа' : 'Get a 24-hour key'}
                  </Button>
                  {issueTrial.isError ? (
                    <p className="lp-check-error">{String(issueTrial.error.message)}</p>
                  ) : null}
                </>
              )}
              <button
                className="lp-btn"
                type="button"
                disabled={connectionNotice === 'checking'}
                onClick={async () => {
                  setConnectionNotice('checking');
                  const currentConnection = await connection.refetch();
                  setConnectionNotice(currentConnection.data?.connected ? 'connected' : 'missing');
                }}
              >
                {connectionNotice === 'checking'
                  ? ru
                    ? 'Проверяем подключение…'
                    : 'Checking connection…'
                  : ru
                    ? 'Я подключился — проверить ещё раз'
                    : 'I connected — check again'}
              </button>
              {connectionNotice === 'missing' ? (
                <p className="lp-connection-notice is-missing" role="alert" aria-live="assertive">
                  <Icon name="alert" size={18} />
                  <span>
                    {ru
                      ? 'Чтобы начать проверку, подключитесь к Uplink VPN. Нет ключа? Зарегистрируйтесь и получите бесплатный доступ на 24 часа, затем повторите проверку.'
                      : 'Connect to Uplink VPN to start the check. No key yet? Sign up for 24 hours of free access, then try again.'}
                  </span>
                </p>
              ) : connectionNotice === 'connected' ? (
                <p className="lp-connection-notice is-connected" role="status" aria-live="polite">
                  <Icon name="check" size={18} />
                  <span>
                    {ru
                      ? 'VPN подключён. Теперь можно запускать тест.'
                      : 'VPN is connected. You can start the test.'}
                  </span>
                </p>
              ) : null}
            </div>
          ) : null}
          <div className={`lp-speed-meter${phase ? ' is-running' : ''}`}>
            <strong>{speed === null ? '—' : speed.toFixed(1)}</strong>
            <span>{ru ? 'Мбит/с, загрузка' : 'Mbps download'}</span>
          </div>
          <div className="lp-service-checks">
            {SERVICES.map(({ key, name }) => (
              <div className={`lp-service-check is-${services[key]}`} key={key}>
                <span className="lp-service-dot" aria-hidden />
                <span>{name}</span>
                <strong>
                  {services[key] === 'checking'
                    ? ru
                      ? 'Проверяем…'
                      : 'Checking…'
                    : services[key] === 'ok'
                      ? ru
                        ? 'Доступен'
                        : 'Available'
                      : services[key] === 'fail'
                        ? ru
                          ? 'Недоступен'
                          : 'Unavailable'
                        : '—'}
                </strong>
              </div>
            ))}
          </div>
          {phase ? <p className="lp-check-progress">{phase}</p> : null}
          {personalWorks !== null ? (
            <p className={`lp-personal-result${personalWorks ? ' is-ok' : ' is-fail'}`}>
              {personalWorks
                ? ru
                  ? 'Проверка завершена: соединение работает.'
                  : 'Check complete: the connection works.'
                : ru
                  ? 'Проверка завершена: есть проблемы с соединением.'
                  : 'Check complete: connection issues found.'}
            </p>
          ) : null}
          {testError || submit.isError ? (
            <p className="lp-check-error">
              {ru
                ? 'Часть проверки выполнить не удалось. Повторите попытку через минуту.'
                : 'Part of the check failed. Try again in a minute.'}
            </p>
          ) : null}
          <button
            className="lp-btn lp-btn--primary lp-btn--lg"
            type="button"
            disabled={phase !== null || connectionNotice === 'checking'}
            onClick={runCheck}
          >
            {phase ?? (ru ? 'Запустить проверку' : 'Start check')}
          </button>
          {checkFeedback ? (
            <p className="lp-check-feedback" role="alert" aria-live="assertive">
              <Icon name="alert" size={18} />
              <span>{checkFeedback}</span>
            </p>
          ) : null}
          {locationVerifiedInRussia ? (
            <p className="lp-check-note">
              {ru
                ? 'После завершения результат сразу обновит последний публичный статус выше.'
                : 'When complete, the result immediately updates the latest public status above.'}
            </p>
          ) : null}
        </section>

        <p className="lp-status-privacy">
          {ru
            ? 'Мы не публикуем IP сервера и название инфраструктурного провайдера. Проверка доступности показывает ответ веб-сервиса и не заменяет проверку всех функций его приложения.'
            : 'We do not publish the server IP or infrastructure provider. Availability checks verify a web response, not every feature of an app.'}
        </p>
        <a className="lp-btn lp-btn--primary lp-btn--lg" href="/">
          {ru ? 'Вернуться на Uplink' : 'Back to Uplink'}
        </a>
      </section>
    </main>
  );
}
